import { addYears, isValid, parseISO } from "date-fns";
import {
  PROFESSIONAL_CARD_CONFIG,
  PROFESSIONAL_CARD_VALIDITY_YEARS,
  type ProfessionalCardContext,
  type ProfessionalCardData,
  type ProfessionalCardExtraction,
} from "@asap/shared/document/document-types/professional-card";
import { ProfessionalCardSchemas } from "@asap/shared/schemas/document/document-types/professional-card";
import { formatDateForDB } from "@asap/shared/utils/date";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { DocumentTypeHandler } from "../handler";

/**
 * Carte BTP Handler (Carte d'identification professionnelle BTP)
 */
export const ProfessionalCardHandler = new DocumentTypeHandler<
  ProfessionalCardExtraction,
  ProfessionalCardData,
  ProfessionalCardContext
>({
  // Identity
  documentType: PROFESSIONAL_CARD_CONFIG.documentType,
  documentName: PROFESSIONAL_CARD_CONFIG.documentName,

  // Constraints
  maxFiles: PROFESSIONAL_CARD_CONFIG.maxFiles,
  acceptedMimeTypes: PROFESSIONAL_CARD_CONFIG.acceptedMimeTypes,
  requiredFields: PROFESSIONAL_CARD_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/professional-card/extract",
    warn: "documents/professional-card/warn",
    verify: "documents/professional-card/verify",
    store: "documents/professional-card/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: ProfessionalCardSchemas.extractResponse,
    warnResponse: ProfessionalCardSchemas.warnResponse,
    validateResponse: ProfessionalCardSchemas.validateResponse,
    storeResponse: ProfessionalCardSchemas.storeResponse,
  },

  // Toast messages
  toastMessages: {
    success: "La carte BTP a bien été validée.",
  },

  // Form fields
  formFields: [
    {
      key: "last_name",
      label: PROFESSIONAL_CARD_CONFIG.fieldLabels.last_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "first_name",
      label: PROFESSIONAL_CARD_CONFIG.fieldLabels.first_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "document_number",
      label: PROFESSIONAL_CARD_CONFIG.fieldLabels.document_number,
      type: "text",
      placeholder: "Ex : 1234567890",
    },
    {
      key: "issue_date",
      label: PROFESSIONAL_CARD_CONFIG.fieldLabels.issue_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "expiration_date",
      label: PROFESSIONAL_CARD_CONFIG.fieldLabels.expiration_date,
      type: "date",
      colSpan: 1,
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: ProfessionalCardExtraction): Partial<ProfessionalCardData> => {
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Les fichiers ajoutés ne semblent pas correspondre à une carte BTP. Vérifie que ce soit bien le cas."
      );
    }

    const result: Partial<ProfessionalCardData> = {};

    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.document_number) result.document_number = extraction.document_number;
    if (extraction.issue_date) {
      result.issue_date = extraction.issue_date;
      const parsedIssueDate = parseISO(extraction.issue_date);
      if (isValid(parsedIssueDate)) {
        result.expiration_date = formatDateForDB(addYears(parsedIssueDate, PROFESSIONAL_CARD_VALIDITY_YEARS));
      }
    }

    return result;
  },
});
